import React, { useState, useMemo } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "../common/tables";
import { SubscriptionLevelLabel as FormattedSubscriptionLevel } from "../../../common/SubscriptionLevelLabel";
import { SUBSCRIPTION_LEVEL } from "../../../../constant/subscriptionLevel";
import {
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Grid,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

/**
 * MergedUserTable
 * Displays a unified table for all users (own + payment liable)
 * with filtering and actions for Betalningsansvar users.
 */
const MergedUserTable = ({  
  ownUsers,
  paymentLiableUsers,
  onDeleteUser,
  historicData
}) => {
  const intl = useIntl();
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [usernameFilter, setUsernameFilter] = useState("");

  
  const [selectedSubscriptionLevels, setSelectedSubscriptionLevels] = useState([]);



  // Merge all users into one dataset
  const mergedRows = useMemo(
    () => {
      return [
        ...(ownUsers || []).map((user) => ({
          ...user,
          company: user.company || "",
          userType: "Intern",
        })),
        ...(paymentLiableUsers || []).map((user) => ({
          ...user,
          company: user.company || "",
          userType: "Betalningsansvar",
        })),
      ]
    },
    [ownUsers, paymentLiableUsers]
  );

  // Unique subscription levels from mergedRows
  const uniqueSubscriptionLevels = useMemo(() => {
    const set = new Set(mergedRows.map((u) => u.subscriptionLevel).filter(Boolean));
    return Array.from(set);
  }, [mergedRows]);

  // Get unique companies for filter dropdown
  const uniqueCompanies = useMemo(() => {
    const set = new Set(mergedRows.map((u) => u.company).filter(Boolean));
    return Array.from(set);
  }, [mergedRows]);


  if (mergedRows.length === 0) return null;

  const renderPayingLabel = (isPaying) =>
    intl.formatMessage({ id: isPaying ? "common.yes" : "common.no" });

  const isPayingUser = (subscriptionLevel) =>
    subscriptionLevel && subscriptionLevel !== SUBSCRIPTION_LEVEL.Free;

  const handleOpenDetails = (user) => {
    setSelectedUser(user);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setSelectedUser(null);
    setDetailsOpen(false);
  };

  const handleDeleteUser = (user) => {
    if (onDeleteUser) onDeleteUser(user);
    else console.log("Delete user:", user);
  };


  // Apply filters
  const filteredRows = mergedRows.filter((row) => {
    const matchesCompany =
      selectedCompanies.length === 0 || selectedCompanies.includes(row.company);
    const matchesUsername =
      !usernameFilter ||
      row.username.toLowerCase().includes(usernameFilter.toLowerCase());
    const matchesSubscriptionLevel =
      selectedSubscriptionLevels.length === 0 ||
      selectedSubscriptionLevels.includes(row.subscriptionLevel);

    return matchesCompany && matchesUsername && matchesSubscriptionLevel;
  });

 

  return (
    <Paper sx={{ p: 2 }}>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="company-filter-label">Företag</InputLabel>
            <Select
              labelId="company-filter-label"
              multiple
              value={selectedCompanies}
              onChange={(e) => setSelectedCompanies(e.target.value)}
              input={<OutlinedInput label="Företag" />}
              renderValue={(selected) =>
                selected.length === 0
                  ? "Välj företag"
                  : `${selected.length} valda`
              }
            >
              {uniqueCompanies.length > 0 ? (
                uniqueCompanies.map((company) => (
                  <MenuItem key={company} value={company}>
                    {company || "(Saknas)"}
                  </MenuItem>
                ))
              ) : (
                  <MenuItem disabled>(Inga företag)</MenuItem>
                )}
            </Select>

            {/* Chips below */}
            {selectedCompanies.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                {selectedCompanies.map((company) => (
                  <Chip
                    key={company}
                    label={company || "(Saknas)"}
                    size="small"
                    onDelete={() =>
                      setSelectedCompanies((prev) =>
                        prev.filter((c) => c !== company)
                      )
                    }
                  />
                ))}
              </Box>
            )}
          </FormControl>          
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Filtrera på användarnamn"
            value={usernameFilter}
            onChange={(e) => setUsernameFilter(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="subscription-filter-label">Prenumerationsnivå</InputLabel>
            <Select
              labelId="subscription-filter-label"
              multiple
              value={selectedSubscriptionLevels}
              onChange={(e) => setSelectedSubscriptionLevels(e.target.value)}
              input={<OutlinedInput label="Prenumerationsnivå" />}
              renderValue={(selected) =>
                selected.length === 0 ? "Välj nivåer" : `${selected.length} valda`
              }
            >
              {uniqueSubscriptionLevels.length > 0 ? (
                uniqueSubscriptionLevels.map((level) => (
                  <MenuItem key={level} value={level}>
                    <FormattedSubscriptionLevel subscriptionLevel={level} />
                  </MenuItem>
                ))
              ) : (
                  <MenuItem disabled>(Inga nivåer)</MenuItem>
                )}
            </Select>

            {/* Chips below */}
            {selectedSubscriptionLevels.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                {selectedSubscriptionLevels.map((level) => (
                  <Chip
                    key={level}
                    label={<FormattedSubscriptionLevel subscriptionLevel={level} />}
                    size="small"
                    onDelete={() =>
                      setSelectedSubscriptionLevels((prev) =>
                        prev.filter((l) => l !== level)
                      )
                    }
                  />
                ))}
              </Box>
            )}
          </FormControl>
        </Grid>
      </Grid>

      {/* Table */}
      <TableContainer sx={{ overflowY: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Företag</TableCell>
              <TableCell>Användare</TableCell>
              <TableCell>Prenumerationsnivå</TableCell>
              <TableCell>Användartyp</TableCell>
              <TableCell>Avgiftsgrundande</TableCell>
              {!historicData && (
              <TableCell align="right">
                <FormattedMessage id="common.actions" defaultMessage="Åtgärder" />
                </TableCell>)}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredRows.map((row) => {
              const isPaying = isPayingUser(row.subscriptionLevel);
              const isPaymentResponsible = row.userType === "Betalningsansvar";

              return (
                <TableRow key={row.userId}>
                  <TableCell>{row.company}</TableCell>
                  <TableCell>{row.username}</TableCell>
                  <TableCell>
                    <FormattedSubscriptionLevel
                      subscriptionLevel={row.subscriptionLevel}
                    />
                  </TableCell>
                  <TableCell>{row.userType}</TableCell>
                  <TableCell>{renderPayingLabel(isPaying)}</TableCell>
                  {!historicData && (
                  <TableCell align="right">
                    {isPaymentResponsible && (
                      <>
                        <Tooltip
                          title={intl.formatMessage({
                            id: "common.details",
                            defaultMessage: "Detaljer",
                          })}
                        >
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDetails(row)}
                          >
                            <InfoOutlinedIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip
                          title={intl.formatMessage({
                            id: "common.delete",
                            defaultMessage: "Ta bort",
                          })}
                        >
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteUser(row)}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    </TableCell>)}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Details modal */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="sm" fullWidth>
        <DialogTitle>
          <FormattedMessage id="common.userDetails" defaultMessage="Användardetaljer" />
        </DialogTitle>
        <DialogContent dividers>
          {selectedUser ? (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {selectedUser.username}
              </Typography>
              <Typography variant="body2">
                Prenumerationsnivå:{" "}
                <FormattedSubscriptionLevel
                  subscriptionLevel={selectedUser.subscriptionLevel}
                />
              </Typography>
              <Typography variant="body2">
                Användartyp: {selectedUser.userType || "Ej angiven"}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Avgiftsgrundande:{" "}
                {renderPayingLabel(isPayingUser(selectedUser.subscriptionLevel))}
              </Typography>

              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Medlem på följande sajter:
          </Typography>

                {selectedUser.sitemembership && selectedUser.sitemembership.length > 0 ? (
                  <ul>
                    {selectedUser.sitemembership.map((site) => (
                      <li key={site.siteId}>
                        {site.name} ({site.siteRole})
                      </li>
                    ))}
                  </ul>
                ) : (
                    <Typography variant="body2">Ingen sajtmembership</Typography>
                  )}
              </Box>
            </>
          ) : (
              <Typography>Ingen användare vald</Typography>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>
            <FormattedMessage id="common.close" defaultMessage="Stäng" />
          </Button>
        </DialogActions>
      </Dialog>

    </Paper>
  );
};

MergedUserTable.defaultProps = {
  className: "",
  ownUsers: [],
  paymentLiableUsers: [],
  onDeleteUser: null,
};

MergedUserTable.propTypes = {
  className: PropTypes.string,
  ownUsers: PropTypes.array,
  paymentLiableUsers: PropTypes.array,
  onDeleteUser: PropTypes.func,
};

export default MergedUserTable;
